import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm5_u4_knx';

const images = [
  { key: 'knx_symbol', src: '/assets/drawable/knx_symbol.png', label: 'KNX Symbol' }
];

const textBlocks = [
  { key: 'block_intro', heading: 'KNX', body: 'KNX ist ein weltweit standardisiertes (ISO/IEC 14543) Kommunikationsprotokoll für die Haus- und Gebäudeautomation. Es ermöglicht die Vernetzung verschiedenster Geräte und Systeme wie Beleuchtung, Sonnenschutz, Heizung, Lüftung, Sicherheitstechnik, Multimedia und mehr über einen gemeinsamen Bus. KNX ist herstellerunabhängig und fördert Interoperabilität, Flexibilität und Investitionssicherheit.' },
  { key: 'block_features', heading: '', body: 'Ein zentrales Merkmal von KNX ist die dezentrale Intelligenz: Jedes Gerät (Sensor, Aktor) kann eigenständig kommunizieren und Entscheidungen treffen, ohne dass eine zentrale Steuereinheit permanent eingeschaltet sein muss. Dies erhöht die Ausfallsicherheit und Flexibilität des Systems.' },
  { key: 'block_technical', heading: 'Technische Details und Funktionsweise', body: 'Die physikalische Schicht von KNX kann über verschiedene Medien realisiert werden: KNX Twisted Pair (TP, die häufigste Variante), KNX Powerline, KNX Radio Frequency (RF/Funk) und KNX IP (über Ethernet). Die Datenübertragung erfolgt über Telegramme (Datenpakete), die sowohl Befehle als auch Statusinformationen enthalten. Geräte besitzen Gruppenadressen, über die sie miteinander kommunizieren. Die Konfiguration erfolgt typischerweise über die Software ETS (Engineering Tool Software).' },
  { key: 'block_components', heading: 'Wichtige KNX-Komponenten', body: '- Sensoren (z.B. Taster, Bewegungsmelder, Wetterstation): Erfassen Ereignisse und senden Telegramme.\n- Aktoren (z.B. Schaltaktoren, Dimmaktoren, Jalousieaktoren): Empfangen Telegramme und steuern Endgeräte.\n- Visualisierung/Bediengeräte (Touchpanels, Tablets mit KNX-App).\n- Systemgeräte (Netzteile, Linienkoppler, USB/IP-Schnittstellen).\n- KNX-Gateways: Zur Integration anderer Protokolle (BACnet, Modbus, DALI etc.).' },
  { key: 'block_advantages', heading: 'Vorteile von KNX', body: '- Offener Standard: Breite Herstellerunterstützung und große Produktvielfalt.\n- Flexibel und skalierbar: Von Einfamilienhäusern bis zu großen Zweckbauten.\n- Dezentral: Erhöhte Ausfallsicherheit.\n- Zukunftssicher: Etablierter Standard mit kontinuierlicher Weiterentwicklung.\n- Umfassende Funktionalität: Ermöglicht die Integration vieler Gewerke in ein System.' }
];

const M5_U4_KNX: React.FC = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { token, canEditTextsM5, canUploadImagesM5 } = useAuth();
  const canEditTexts = canEditTextsM5;
  const canUploadImages = canUploadImagesM5;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(
    () => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e) {
      console.error('Text-Speichern fehlgeschlagen:', e);
      alert('Fehler beim Speichern.');
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e) {
      console.error('Bild-Upload fehlgeschlagen:', e);
      alert('Fehler beim Upload.');
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '5.4 KNX', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, images.map(img => React.createElement(ZoomableImage, { key: img.key, src: imgSrc(img.key, img.src), label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined })) ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          mergedBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key })),
          React.createElement(Box, { sx: { mt: 3 } },
            React.createElement('div', { style: { fontSize: '16px', fontWeight: 600, marginBottom: '12px' } }, 'Hier gibt es die besten Schulungs-Videos um KNX zu erlernen'),
            React.createElement('div', { style: { position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' } },
              React.createElement('iframe', {
                src: 'https://www.youtube.com/embed/videoseries?list=PLQot1M29bpxwXc9u4Nb5WtS7qBJTFCoGN',
                style: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' },
                allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
                allowFullScreen: true
              })
            )
          )
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m5_u99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M5_U4_KNX;

